import React, { useState } from 'react';
import {
  Box,
  CssBaseline,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Breadcrumbs,
  Link,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab
} from '@mui/material';
import {
  Menu as MenuIcon,
  ArrowBack as ArrowBackIcon,
  Home as HomeIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';
import Header from './Header';
import Sidebar from './Sidebar';
import CastcoLogo from '../CastcoLogo';

interface PageLayoutProps {
  children: React.ReactNode;
  title?: string;
  showBackButton?: boolean;
  backPath?: string;
  breadcrumbs?: Array<{ label: string; path?: string }>;
}

const PageLayout: React.FC<PageLayoutProps> = ({
  children,
  title,
  showBackButton = false,
  backPath,
  breadcrumbs = []
}) => {
  const [sidebarOpen, setSidebarOpen] = useState(false); // 默認關閉
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  // 導航菜單項目
  const navItems = [
    { label: 'CASTCO', path: '/', isLogo: true },
    { label: t('navigation.rfidManagement'), path: '/rfid' },
    { label: t('navigation.uSlump'), path: '/site-slump' },
    { label: t('navigation.contractData'), path: '/contract-data' },
    // { label: 'Grout', path: '/grout' },
    { label: t('navigation.mixID'), path: '/mixid' },
    //{ label: t('navigation.daily'), path: '/daily-report' },
    { label: t('navigation.first3End3'), path: '/first3-end3' }
  ];

  // 在移動設備上默認關閉側邊欄
  React.useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [isMobile]);

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleBackClick = () => {
    if (backPath) {
      navigate(backPath);
    } else {
      navigate(-1);
    }
  };

  const handleBreadcrumbClick = (path?: string) => {
    if (path) {
      navigate(path);
    }
  };

  const handleNavItemClick = (path: string) => {
    navigate(path);
  };

  // 獲取當前選中的導航項目索引
  const getCurrentTabIndex = () => {
    const currentPath = location.pathname;
    const index = navItems.findIndex(item => item.path === currentPath);
    return index >= 0 ? index : 0;
  };

  // 生成麵包屑導航
  const generateBreadcrumbs = () => {
    const defaultBreadcrumbs = [
      { label: t('navigation.home'), path: '/' },
      ...breadcrumbs
    ];

    return defaultBreadcrumbs;
  };

  const breadcrumbItems = generateBreadcrumbs();

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      <CssBaseline />

      {/* 側邊欄 */}
      <Sidebar
        open={sidebarOpen}
        onToggle={handleSidebarToggle}
        width={240}
      />

      {/* 主內容區域 */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        {/* 頂部導航欄 */}
        <AppBar
          position="static"
          elevation={0}
          sx={{
            backgroundColor: 'background.paper',
            borderBottom: '1px solid',
            borderColor: 'divider',
            zIndex: theme.zIndex.drawer - 1
          }}
        >
          <Toolbar sx={{ minHeight: '64px !important' }}>
            {/* 側邊欄切換按鈕 */}
            {/* <IconButton
              edge="start"
              color="inherit"
              aria-label="toggle sidebar"
              onClick={handleSidebarToggle}
              sx={{ mr: 2, color: 'text.primary' }}
            >
              <MenuIcon />
            </IconButton> */}

            <Box sx={{ borderBottom: '0px solid #e0e0e0' , flexGrow: 1, display: { xs: 'none', sm: 'block' }}}>
              <Tabs
                value={getCurrentTabIndex()}
                onChange={(event, newValue) => {
                  if (navItems[newValue]) {
                    handleNavItemClick(navItems[newValue].path);
                  }
                }}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#1976d2',
                    height: '3px'
                  },
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontSize: '16px',
                    fontWeight: 500,
                    color: '#666',
                    minHeight: '48px',
                    padding: '12px 16px',
                    '&.Mui-selected': {
                      color: '#1976d2',
                      fontWeight: 600
                    },
                    '&:hover': {
                      color: '#1976d2',
                      backgroundColor: 'rgba(25, 118, 210, 0.04)'
                    }
                  }
                }}
              >
                {navItems.map((item, index) => (
                  <Tab
                    key={index}
                    label={
                      item.isLogo ? (
                        <CastcoLogo
                          size="small"
                          variant="header"
                          onClick={() => handleNavItemClick(item.path)}
                        />
                      ) : (
                        item.label
                      )
                    }
                    onClick={() => handleNavItemClick(item.path)}
                    sx={item.isLogo ? {
                      minWidth: 'auto',
                      padding: '8px 12px',
                      '&.Mui-selected': {
                        backgroundColor: 'transparent'
                      }
                    } : {}}
                  />
                ))}
              </Tabs>
            </Box>

            {/* 用戶信息區域 */}
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Header />
            </Box>
          </Toolbar>

        </AppBar>

        {/* 主內容 */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            overflow: 'auto',
            backgroundColor: 'background.default',
            minHeight: 0
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default PageLayout;